import { useHistory } from 'react-router';
import { useMutation, useQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState } from '@/recoil/auth';
import GlobalAlert from '@/components/GlobalAlert';
import { AxiosError } from 'axios';
import api from '@/apis/auth';
import { getErrorMessage } from '@/utils/error';
import { ChangePasswordForm, FindAccountForm, LoginForm, SignupForm } from '@/apis/auth/interface';
import { pharmacyState } from '@/recoil/pharmacy';
import React from 'react';
import { addPharmacistDevices, getPharmacistDevices } from '@/utils/graphqlApis';
import theme from '@/styles/theme';

const useAuthQuery = () => {
  const history = useHistory();
  const [auth, setAuth] = useRecoilState(authState);
  const pharmacy = useRecoilValue(pharmacyState);

  /** [계정] 로그인 */
  const loginMutation = useMutation(
    (loginForm: LoginForm) => api.login(loginForm),
    {
      onSuccess: async res => {
        setAuth({
          ...auth,
          token: res.result.token
        });
      },
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [계정] 회원가입 */
  const signupMutation = useMutation(
    (signupForm: SignupForm) => api.signup(signupForm),
    {
      onSuccess: () => {
        GlobalAlert.show({
          title: '회원가입이 완료 되었습니다',
          body: (
            <div>
              {'가이드를 확인하신 후\n' +
                '담당 매니저나 고객센터로 연락해주세요.'}
            </div>
          ),
          buttons: [
            {
              text: <span style={{ color: theme.DF_COLOR.PRIMARY500 }}>{'가이드 보기'}</span>,
              onClick: () => {
                window.open(
                  'https://sites.google.com/doctornow.co.kr/guide-pharmacy/pharmacy-guide/4',
                  'onbording-quide'
                );
              }
            }
          ]
        });
        history.replace('/');
      },
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [계정] 아이디 찾기 */
  const findIdMutation = useMutation(
    (findAccountForm: FindAccountForm) => api.findId(findAccountForm),
    {
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [계정] 비밀번호 찾기 (비밀번호 변경 토큰 발급) */
  const findPasswordMutation = useMutation(
    (findAccountForm: FindAccountForm) => api.findPassword(findAccountForm),
    {
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [계정] 비밀번호 변경 */
  const changePasswordMutation = useMutation(
    (changePasswordForm: ChangePasswordForm) =>
      api.changePassword(changePasswordForm),
    {
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [알림] 디바이스 조회 */
  const devicesQuery = useQuery(
      ['devices'],
      () => getPharmacistDevices(auth.token),
      {
        enabled: !!pharmacy.id && !!auth.token,
        onSuccess: (result: any) => {
          setAuth((prevAuth: any) => ({
            ...prevAuth,
            devices: result.getPharmacy?.pharmacist?.devices ?? []
          }));
        }
      }
    );

  /** [알림] 디바이스 업데이트*/
  const devicesUpdateMutation = useMutation(
    (firebaseToken: string) =>
      addPharmacistDevices(auth.token,
        {
          alias: 'WEB',
          deviceId: pharmacy.account.username,
          firebaseToken: firebaseToken,
        }
      ),
    {
      onSuccess: (result: any) => {
        setAuth({ ...auth, devices: [...auth.devices, result] });
      }
    }
  );

  return {
    loginMutation,
    signupMutation,
    findIdMutation,
    findPasswordMutation,
    changePasswordMutation,
    devicesQuery,
    devicesUpdateMutation
  };
};

export default useAuthQuery;
